// Generator Services Section Component
import { useState, useEffect } from 'react';
import { FiWind, FiTool, FiClock, FiZap } from 'react-icons/fi';
import { MdOutlineDescription, MdPhone, MdEmail } from 'react-icons/md';

const GeneratorServicesSection = ({ onDataUpdate, initialData }) => {
    const [generatorServiceType, setGeneratorServiceType] = useState(
        initialData?.generatorServiceType || 'supply'
    );
    const [generatorKvaRange, setGeneratorKvaRange] = useState(
        initialData?.generatorKvaRange || 'medium'
    );
    const [generatorPurpose, setGeneratorPurpose] = useState(
        initialData?.generatorPurpose || 'residential'
    );
    const [generatorFuelType, setGeneratorFuelType] = useState(
        initialData?.generatorFuelType || 'diesel'
    );
    const [generatorHireDuration, setGeneratorHireDuration] = useState(
        initialData?.generatorHireDuration || 'weekly'
    );
    const [generatorAdditionalDetails, setGeneratorAdditionalDetails] = useState(
        initialData?.generatorAdditionalDetails || ''
    );

    // Service type definitions
    const serviceTypes = [
        {
            id: 'supply',
            label: 'Generator Supply & Installation',
            desc: 'Professional supply and installation of standby or portable generators',
            icon: <FiWind className="text-2xl" />,
        },
        {
            id: 'maintenance',
            label: 'Generator Maintenance & Servicing',
            desc: 'Comprehensive maintenance to keep your generator ready for emergencies',
            icon: <FiTool className="text-2xl" />,
        },
        {
            id: 'hire',
            label: 'Generator Hire & Rental',
            desc: 'Flexible rental solutions for events, construction sites or backup needs',
            icon: <FiClock className="text-2xl" />,
        },
        {
            id: 'transfer_switch',
            label: 'Automatic Transfer Switch',
            desc: 'Install automatic transfer switches for seamless power switching',
            icon: <FiZap className="text-2xl" />,
        },
    ];

    // kVA size options with base costs per service type
    const kvaRanges = [
        { id: 'small', label: 'Small (5–20 kVA)' },
        { id: 'medium', label: 'Medium (20–100 kVA)' },
        { id: 'large', label: 'Large (100–500 kVA)' },
        { id: 'industrial', label: 'Industrial (500+ kVA)' },
    ];

    const purposeOptions = [
        { id: 'residential', label: 'Residential Backup' },
        { id: 'commercial', label: 'Commercial Backup' },
        { id: 'industrial', label: 'Industrial Backup' },
        { id: 'events', label: 'Events & Temporary Power' },
        { id: 'construction', label: 'Construction Site' },
    ];

    const fuelTypes = [
        { id: 'diesel', label: 'Diesel' },
        { id: 'petrol', label: 'Petrol' },
        { id: 'natural_gas', label: 'Natural Gas' },
        { id: 'dual_fuel', label: 'Dual Fuel' },
    ];

    const hireDurations = [
        { id: 'daily', label: 'Daily' },
        { id: 'weekly', label: 'Weekly' },
        { id: 'monthly', label: 'Monthly' },
        { id: 'long_term', label: 'Long-term (3+ months)' },
    ];


    useEffect(() => {
        if (onDataUpdate) {
            onDataUpdate({
                generatorServiceType,
                generatorKvaRange,
                generatorPurpose,
                generatorFuelType,
                generatorHireDuration: generatorServiceType === 'hire' ? generatorHireDuration : null,
                generatorAdditionalDetails,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generatorServiceType, generatorKvaRange, generatorPurpose, generatorFuelType, generatorHireDuration, generatorAdditionalDetails]);

    return (
        <div className="bg-white rounded-xl shadow-lg p-8" data-aos="fade-up">
            {/* Header */}
            <div className="flex items-center mb-8">
                <div className="p-3 bg-orange-100 rounded-lg mr-4">
                    <FiWind className="text-3xl text-orange-600" />
                </div>
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">Generator Services Consultation</h2>
                    <p className="text-gray-600">Select your generator service and provide details for an accurate estimate</p>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
                {/* Left Column - Inputs */}
                <div className="space-y-6">
                    {/* Service Type Selection */}
                    <div>
                        <label className="block text-gray-700 font-medium mb-4">Generator Service Type</label>
                        <div className="space-y-3">
                            {serviceTypes.map(svc => (
                                <label key={svc.id} className="block">
                                    <div className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                        generatorServiceType === svc.id
                                            ? 'border-orange-500 bg-orange-50'
                                            : 'border-gray-300 hover:border-orange-300'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="generatorServiceType"
                                            value={svc.id}
                                            checked={generatorServiceType === svc.id}
                                            onChange={e => setGeneratorServiceType(e.target.value)}
                                            className="h-5 w-5 text-orange-600 mr-4"
                                        />
                                        <div className={`mr-3 ${generatorServiceType === svc.id ? 'text-orange-600' : 'text-gray-500'}`}>
                                            {svc.icon}
                                        </div>
                                        <div className="flex-1">
                                            <div className="font-medium text-gray-800">{svc.label}</div>
                                            <div className="text-sm text-gray-500">{svc.desc}</div>
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Generator Size */}
                    <div>
                        <label className="block text-gray-700 font-medium mb-4">Generator Size (kVA)</label>
                        <div className="space-y-3">
                            {kvaRanges.map(kva => (
                                <label key={kva.id} className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-orange-300">
                                    <input
                                        type="radio"
                                        name="generatorKvaRange"
                                        value={kva.id}
                                        checked={generatorKvaRange === kva.id}
                                        onChange={e => setGeneratorKvaRange(e.target.value)}
                                        className="h-5 w-5 text-orange-600 mr-4"
                                    />
                                    <div className="font-medium text-gray-800">{kva.label}</div>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                    {/* Purpose */}
                    <div>
                        <label className="block text-gray-700 font-medium mb-4">Purpose / Use Case</label>
                        <div className="grid grid-cols-2 gap-3">
                            {purposeOptions.map(purpose => (
                                <label key={purpose.id} className="relative">
                                    <input
                                        type="radio"
                                        name="generatorPurpose"
                                        value={purpose.id}
                                        checked={generatorPurpose === purpose.id}
                                        onChange={e => setGeneratorPurpose(e.target.value)}
                                        className="sr-only peer"
                                    />
                                    <div className={`p-4 border-2 rounded-lg text-center cursor-pointer transition-all text-sm font-medium ${
                                        generatorPurpose === purpose.id
                                            ? 'border-orange-500 bg-orange-50 text-orange-700'
                                            : 'border-gray-300 text-gray-800 hover:border-orange-300'
                                    }`}>
                                        {purpose.label}
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Fuel Type - shown for supply and maintenance */}
                    {(generatorServiceType === 'supply' || generatorServiceType === 'maintenance') && (
                        <div>
                            <label className="block text-gray-700 font-medium mb-4">Fuel Type</label>
                            <div className="grid grid-cols-2 gap-3">
                                {fuelTypes.map(fuel => (
                                    <label key={fuel.id} className="relative">
                                        <input
                                            type="radio"
                                            name="generatorFuelType"
                                            value={fuel.id}
                                            checked={generatorFuelType === fuel.id}
                                            onChange={e => setGeneratorFuelType(e.target.value)}
                                            className="sr-only peer"
                                        />
                                        <div className={`p-3 border-2 rounded-lg text-center cursor-pointer transition-all text-sm font-medium ${
                                            generatorFuelType === fuel.id
                                                ? 'border-orange-500 bg-orange-50 text-orange-700'
                                                : 'border-gray-300 text-gray-800 hover:border-orange-300'
                                        }`}>
                                            {fuel.label}
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Hire Duration - shown only for hire */}
                    {generatorServiceType === 'hire' && (
                        <div>
                            <label className="block text-gray-700 font-medium mb-4">Hire Duration</label>
                            <div className="space-y-3">
                                {hireDurations.map(dur => (
                                    <label key={dur.id} className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-orange-300">
                                        <input
                                            type="radio"
                                            name="generatorHireDuration"
                                            value={dur.id}
                                            checked={generatorHireDuration === dur.id}
                                            onChange={e => setGeneratorHireDuration(e.target.value)}
                                            className="h-5 w-5 text-orange-600 mr-4"
                                        />
                                        <div className="font-medium text-gray-800">{dur.label}</div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Additional Details */}
                    <div>
                        <label className="block text-gray-700 font-medium mb-4">
                            <div className="flex items-center mb-2">
                                <MdOutlineDescription className="mr-2" />
                                Additional Details
                            </div>
                        </label>
                        <textarea
                            value={generatorAdditionalDetails}
                            onChange={e => setGeneratorAdditionalDetails(e.target.value)}
                            className="w-full h-32 px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                            placeholder="Describe your specific requirements, existing setup, site access details, or any other relevant information..."
                        />
                    </div>

                </div>
            </div>

            {/* Contact Info Banner */}
            <div className="mt-8 p-5 bg-orange-50 border border-orange-200 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-2 text-orange-700 font-semibold text-sm shrink-0">
                    <FiWind className="text-xl" />
                    Need help? Contact us directly:
                </div>
                <div className="flex flex-col sm:flex-row gap-3 text-sm">
                    <a
                        href="tel:+26825185414"
                        className="flex items-center gap-2 text-orange-800 font-medium hover:text-orange-600 transition-colors"
                    >
                        <MdPhone className="text-base shrink-0" />
                        +268 2518 5414
                    </a>
                    <a
                        href="tel:+26825187424"
                        className="flex items-center gap-2 text-orange-800 font-medium hover:text-orange-600 transition-colors"
                    >
                        <MdPhone className="text-base shrink-0" />
                        +268 2518 7424
                    </a>
                    <a
                        href="mailto:info@alternative.co.sz"
                        className="flex items-center gap-2 text-orange-800 font-medium hover:text-orange-600 transition-colors"
                    >
                        <MdEmail className="text-base shrink-0" />
                        info@alternative.co.sz
                    </a>
                </div>
            </div>
        </div>
    );
};

export default GeneratorServicesSection;